/**
 * GET /v1/athletics-availability/teams — coach/admin explorer read of a
 * school's resolved athletic teams with their rosters + coaches.
 *
 * A "resolved athletic team" is a `Section` that an `AthleticGame` resolves onto
 * via the internal `AthleticGame.resolvedSectionId` FK (never a raw Blackbaud
 * id, KTD1/KTD5). The roster is the current ACTIVE STUDENT `SectionMembership`;
 * coaches are the current ACTIVE FACULTY/STAFF members of the same section. Used
 * to validate the Phase-1 data pipeline in the web school-admin view
 * (plan U6/U7, R12/R17).
 *
 * Wire shape only — every id is a public CUID, never an internal DB bigint.
 * Names are Blackbaud-sourced and rendered as plain text (escaped at the web
 * boundary).
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-16-003-feat-athletics-availability-rsvp-plan.md (U6).
 */

import { z } from 'zod'

export const ListAthleticTeamsRequestSchema = z.object({})
export type ListAthleticTeamsRequest = z.infer<typeof ListAthleticTeamsRequestSchema>

/** One roster member (athlete) on a team. */
export const AthleticTeamRosterMemberSchema = z.object({
  /** Public CUID of the roster member (school-user). */
  userId: z.string(),
  /** Display name for the roster member. */
  name: z.string().nullable().optional()
})
export type AthleticTeamRosterMember = z.infer<typeof AthleticTeamRosterMemberSchema>

/** One coach (FACULTY/STAFF member) on a team. */
export const AthleticTeamCoachSchema = z.object({
  /** Public CUID of the coach (school-user). */
  userId: z.string(),
  /** Display name for the coach. */
  name: z.string().nullable().optional(),
  /** The coach's staff function within the section (e.g. COACH), when known. */
  staffRole: z.string().nullable().optional()
})
export type AthleticTeamCoach = z.infer<typeof AthleticTeamCoachSchema>

/** A resolved athletic team — a section with its roster + coaches. */
export const AthleticTeamSchema = z.object({
  /** Public CUID of the resolved section (the "team"). */
  sectionId: z.string(),
  /** Section name (team name). */
  name: z.string().nullable().optional(),
  /** Count of synced games/practices resolving onto this team. */
  gameCount: z.number().int(),
  /** Current ACTIVE STUDENT roster. */
  roster: z.array(AthleticTeamRosterMemberSchema),
  /** Current ACTIVE FACULTY/STAFF coaches. */
  coaches: z.array(AthleticTeamCoachSchema)
})
export type AthleticTeam = z.infer<typeof AthleticTeamSchema>

export const ListAthleticTeamsResponseSchema = z.object({
  data: z.array(AthleticTeamSchema)
})
export type ListAthleticTeamsResponse = z.infer<typeof ListAthleticTeamsResponseSchema>
